import React, { useState, useEffect, useCallback } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FaCheck, FaPen } from 'react-icons/fa6';
import { MdOutlineAdd } from 'react-icons/md';
import toast from 'react-hot-toast';

import Loader from './custom/Loader';
import { integrationData } from '../utils/constants';
import {
  ENABLE_INTEGRATION_GMAIL,
  ENABLE_INTEGRATION_GOOGLE_CALENDAR,
  ENABLE_INTEGRATION_GOOGLE_DRIVE,
  ENABLE_INTEGRATION_GOOGLE_DOCS,
  ENABLE_INTEGRATION_GOOGLE_SHEETS,
  ENABLE_INTEGRATION_GOOGLE_FORMS,
  ENABLE_INTEGRATION_GOOGLE_KEEP,
  ENABLE_INTEGRATION_GOOGLE_TASKS,
  ENABLE_INTEGRATION_GOOGLE_DIRECTORY,
  ENABLE_INTEGRATION_GOOGLE_VISION,
  ENABLE_INTEGRATION_YOUTUBE,
  ENABLE_INTEGRATION_MICROSOFT_OUTLOOK,
  ENABLE_INTEGRATION_GOHIGHLEVEL,
  ENABLE_INTEGRATION_RAZORPAY,
  ENABLE_INTEGRATION_SHIPROCKET,
} from '../config/features';
import { authenticateApp } from '../utils/auth'; // Your auth.js
import { getAuthenticatedApps, unauthApp } from '../services/api';
import ResourceModal from './modals/ResourceModal';
import { useEmails } from '../context/EmailsProvider';
import { DataState } from '../context/DataProvider';
import { useTeams } from '../context/TeamsProvider';
import { useAccess } from '../services/access';
import McpConnectionManager from './McpConnections/McpConnectionManager';
import { useMCP } from '../context/McpProvider';
import { ENABLE_MCP } from '../config/features';
import WorkEmailCapture from './premiumFeatureComponent/WorkEmailCapture';
import JiraResourceModal from './modals/JiraResourceModal';
import ConfluenceResourceModal from './modals/ConfluenceResourceModal';
import SharePointAccessModal from './modals/SharePointAccessModal';
import OutlookSyncModal from './modals/OutlookSyncModal';
import SharePointSitesModal from './modals/SharePointSitesModal';
import GohighlevelResourceModal from './modals/GohighlevelResourceModal';
import QuickBooksEnvModal from './modals/QuickBooksEnvModal';
import ShiprocketAuthModal from './modals/ShiprocketAuthModal';
import RazorpayAuthModal from './modals/RazorpayAuthModal';

const Integration = ({
  onClose,
  user_google_id,
  bot_id,
  onlyEmailApps = false,
  isTeamsInitialized,
}) => {
  const [loading, setLoading] = useState(false);
  const [integratedApps, setIntegratedApps] = useState([]);
  const [notIntegratedApps, setNotIntegratedApps] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAppName, setModalAppName] = useState('');
  const [isQuickBooksEnvModalOpen, setIsQuickBooksEnvModalOpen] =
    useState(false);
  const [isSharePointAccessOpen, setIsSharePointAccessOpen] = useState(false);
  const [isOutlookSyncOpen, setIsOutlookSyncOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(null); // unique_index
  const [searchTerm, setSearchTerm] = useState('');
  const [showPremium, setShowPremium] = useState(false);
  const { refreshConnections } = useMCP();
  const [activeTab, setActiveTab] = useState('Apps');

  // NEW: State for controlling the MCP Connection Manager modal
  const isMcpModalOpen = false;

  const { userInfo } = DataState();
  const { can } = useAccess();
  const canConnect = can('integration:connect');
  const canDisconnect = can('integration:disconnect');
  const { isTeamsInitialized: isTeamsInitFromDataState } = useTeams();
  const { setGmailAuthStatus, setOutlookAuthStatus } = useEmails();

  // Use the prop if passed, otherwise fallback to context or assume false
  const inTeams =
    typeof isTeamsInitialized === 'boolean'
      ? isTeamsInitialized
      : isTeamsInitFromDataState || false;

  //preparing apps_data based on onlyEmailApps flag
  let apps_data = [...integrationData];

  if (onlyEmailApps) {
    const emailApps = [
      ...(ENABLE_INTEGRATION_MICROSOFT_OUTLOOK ? ['microsoft_outlook'] : []),
      ...(ENABLE_INTEGRATION_GMAIL ? ['gmail'] : []),
    ];
    apps_data = apps_data.filter((app) => emailApps.includes(app.value));
  } else {
    apps_data = apps_data.map((app, unique_index) => {
      app.unique_index = unique_index;
      return app;
    });
  }

  const fetchIntegratedApps = async (selectedAppForIntegration = null) => {
    setLoading(true);
    try {
      const result = await getAuthenticatedApps(user_google_id, bot_id);
      const integrated = result.apps;

      // here I want to insert the redirect_url from the result into each app in apps client config state
      const updatedAppsClientConfig = { ...(result.apps_client_config || {}) };
      integrated.forEach((app) => {
        if (result.apps_client_config[app]) {
          updatedAppsClientConfig[app] = {
            client_id: result.apps_client_config[app].client_id || '',
            client_secret: result.apps_client_config[app].client_secret || '',
            use_user_client:
              result.apps_client_config[app].use_user_client || false,
            redirect_uri: result.redirect_uri || '',
          };
        }
      });

      const integratedAppsFiltered = apps_data.filter((app) =>
        integrated.includes(app.value),
      );
      const notIntegratedAppsFiltered = apps_data.filter(
        (app) => !integrated.includes(app.value),
      );
      setIntegratedApps(integratedAppsFiltered);
      setNotIntegratedApps(notIntegratedAppsFiltered);
    } catch (error) {
      toast.error('Failed to fetch integrated apps');
    } finally {
      refreshConnections();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegratedApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_google_id, bot_id]);

  useEffect(() => {
    if (apps_data.length === 0 && activeTab === 'Apps') {
      if (ENABLE_MCP) setActiveTab('Custom MCP');
    }
  }, [apps_data.length, activeTab]);

  // Global keydown handler for building the search term
  const handleKeyDown = useCallback(
    (e) => {
      // NEW: Disable search listener if any modal or premium view is open
      if (isModalOpen || isMcpModalOpen || showPremium) return;

      // Ignore if any modifier keys are pressed
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      // if (e.key === "Backspace") {
      //   setSearchTerm((prev) => prev.slice(0, -1));
      if (e.key === 'Backspace' || e.key === 'Delete') {
        setSearchTerm('');
      } else if (e.key && e.key.length === 1) {
        // Append all printable characters including space
        setSearchTerm((prev) => prev + e.key);
      }
    },
    [isModalOpen, isMcpModalOpen, showPremium], // Add these dependencies so the listener updates when state changes
  );
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Filter apps based on a fuzzy regex match from the search term.
  const filterApps = (apps) => {
    if (!searchTerm) return apps;
    try {
      // Create a fuzzy regex pattern, e.g. "olook" => "o.*l.*o.*o.*k"
      const pattern = searchTerm.split('').join('.*');
      const regex = new RegExp(pattern, 'i');
      return apps.filter((app) => regex.test(app.label));
    } catch (error) {
      error('Regex error:', error);
      return apps;
    }
  };

  // -------- AUTH --------
  const handleIntegrateButtonClick = async (app) => {
    const googleApps = [
      ...(ENABLE_INTEGRATION_GMAIL ? ['gmail'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_DRIVE ? ['google_drive'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_CALENDAR ? ['google_calendar'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_DOCS ? ['google_docs'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_SHEETS ? ['google_sheets'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_FORMS ? ['google_forms'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_TASKS ? ['google_tasks'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_KEEP ? ['google_keep'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_DIRECTORY ? ['google_directory'] : []),
      ...(ENABLE_INTEGRATION_GOOGLE_VISION ? ['google_vision'] : []),
      ...(ENABLE_INTEGRATION_YOUTUBE ? ['youtube'] : []),
    ];

    if (
      googleApps.includes(app.value) &&
      (!userInfo || !userInfo.enterprise_access)
    ) {
      setShowPremium(true);
      return;
    }

    if (app.value === 'shiprocket') {
      setModalAppName('shiprocket');
      setIsModalOpen(true);
      return;
    }

    if (app.value === 'razorpay') {
      setModalAppName('razorpay');
      setIsModalOpen(true);
      return;
    }

    if (app.value === 'quickbooks') {
      setIsQuickBooksEnvModalOpen(true);
      return;
    }

    if (app.value === 'microsoft_sharepoint') {
      setIsSharePointAccessOpen(true);
      return;
    }

    if (app.value === 'microsoft_outlook') {
      setIsOutlookSyncOpen(true);
      return;
    }

    setLoading(true);

    await authenticateApp(
      app.value,
      user_google_id,
      async () => {
        await fetchIntegratedApps();
        setLoading(false);
      },
      (appName) => {
        if (onlyEmailApps && appName === 'gmail') {
          setGmailAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
        if (onlyEmailApps && appName === 'microsoft_outlook') {
          setOutlookAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
      },
      inTeams,
    );
  };

  // SharePoint: the access the member chose decides the Microsoft scopes.
  // Signs in to Microsoft with the chosen access; resolves when the popup
  // is done. Also how access is changed later (Microsoft returns at once
  // when nothing new is asked for).
  const connectSharePoint = (sharepointAccess) =>
    new Promise((resolve) => {
      authenticateApp(
        'microsoft_sharepoint',
        user_google_id,
        () => {},
        () => {},
        inTeams,
        {
          sharepointAccess,
          // Success, error, closed or blocked popup: never leave a spinner.
          onDone: async () => {
            await fetchIntegratedApps();
            resolve();
          },
        },
      );
    });

  const handleSharePointContinue = async (sharepointAccess) => {
    setIsSharePointAccessOpen(false);
    setLoading(true);
    await connectSharePoint(sharepointAccess);
    setLoading(false);
  };

  // Outlook: how much existing mail the first sync brings in.
  const handleOutlookSyncContinue = async (mailSync) => {
    setIsOutlookSyncOpen(false);
    setLoading(true);
    await authenticateApp(
      'microsoft_outlook',
      user_google_id,
      async () => {
        await fetchIntegratedApps();
        setLoading(false);
      },
      // From any Integrations modal, so an open Emails tab picks it up.
      () => setOutlookAuthStatus((p) => ({ status: true, state: p.state + 1 })),
      inTeams,
      { mailSync },
    );
  };

  const handleQuickBooksEnvSelect = async (envType) => {
    setIsQuickBooksEnvModalOpen(false);
    setLoading(true);
    await authenticateApp(
      'quickbooks',
      user_google_id,
      async () => {
        await fetchIntegratedApps();
        setLoading(false);
      },
      (appName) => {},
      inTeams,
      { envType },
    );
  };

  const handleUnAuthorisationButtonClick = async (selectedAppForUnauth) => {
    if (selectedAppForUnauth) {
      setLoading(true);
      await unauthApp(selectedAppForUnauth.value, bot_id, user_google_id);
      if (onlyEmailApps) {
        if (selectedAppForUnauth.value === 'gmail') {
          setGmailAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        } else if (selectedAppForUnauth.value === 'microsoft_outlook') {
          setOutlookAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        }
      }
      fetchIntegratedApps();
    }
  };

  const openModal = (appName) => {
    setModalAppName(appName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalAppName('');
  };

  const filteredIntegratedApps = filterApps(integratedApps);
  const filteredNotIntegratedApps = filterApps(notIntegratedApps);

  const TABS = [
    ...(apps_data.length > 0 ? ['Apps'] : []),
    ...(ENABLE_MCP ? ['Custom MCP'] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <button
        className="absolute inset-0 block bg-black opacity-50"
        onClick={onClose}
      />

      {showPremium ? (
        <div className="z-10 w-[90%] max-w-6xl">
          <WorkEmailCapture setShowPremium={setShowPremium} />
        </div>
      ) : (
        <div
          className={`bg-1 text-p-text relative z-10 flex h-[90%]      w-[90%] max-w-3xl flex-col rounded-2xl border border-zinc-700
         ${onlyEmailApps ? 'h-fit' : 'max-h-[70%] overflow-hidden'}
         `}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-zinc-700 px-6 py-5">
            <h2 className="text-xl font-semibold text-zinc-200">
              {onlyEmailApps ? 'Your MailBoxes' : 'Your Integrations'}
            </h2>
            <button onClick={onClose}>
              <IoMdClose className="text-zinc-400" size={20} />
            </button>
          </div>

          {/* Tabs + Search */}
          <div className="flex items-center justify-between border-b border-zinc-700 px-6 pt-4">
            <div className="flex gap-8">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm ${
                    activeTab === tab
                      ? 'border-b-2 border-white text-white'
                      : 'text-zinc-400'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="max-h-[65vh] overflow-y-auto px-6 py-6">
            {loading ? (
              <div
                className={`text-p-text flex items-center justify-center pl-4 pr-4  ${onlyEmailApps ? '' : 'h-64'} `}
              >
                <span className="h-12">
                  <Loader />
                </span>
              </div>
            ) : (
              <>
                {/* ===================== APPS TAB ===================== */}
                {activeTab === 'Apps' && (
                  <div
                    className={`flex flex-wrap gap-x-4 gap-y-4 max-sm:flex-col max-sm:items-center ${onlyEmailApps ? 'items-center justify-center pt-0' : ''}`}
                  >
                    {filteredIntegratedApps.map((item) => (
                      <div
                        key={item.unique_index}
                        className="group flex w-fit items-center gap-2 rounded-full border border-zinc-400 p-1"
                      >
                        <div className="rounded-full bg-zinc-200 p-1">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="h-5 w-5"
                          />
                        </div>
                        <div className="text-p-text text-md ml-1 text-center font-semibold">
                          {item.label}
                        </div>
                        {[
                          'jira',
                          'confluence',
                          'azure_devops',
                          // Outlook and Teams have none: mail and chats sync on their
                          // own (webhooks, catch-up), with Sync Email on the Emails tab.
                          'github',
                          'gmail',
                          'microsoft_sharepoint',
                          ...(ENABLE_INTEGRATION_GOHIGHLEVEL
                            ? ['gohighlevel']
                            : []),
                        ].includes(item.value) && (
                          <button
                            onClick={() => openModal(item.value)}
                            className="ml-3 rounded-full bg-white/80 p-2 text-blue-700 transition-all duration-300 hover:bg-white hover:text-blue-800"
                          >
                            <FaPen size={10} />
                          </button>
                        )}
                        {/* The tick is the connected status; clicking it asks
                            before disconnecting (one stray click used to
                            drop the connection). */}
                        {confirmDisconnect === item.unique_index ? (
                          <span className="flex items-center gap-1 pr-1 text-xs">
                            <span className="text-p-text">Disconnect?</span>
                            <button
                              onClick={() => {
                                setConfirmDisconnect(null);
                                handleUnAuthorisationButtonClick(item);
                              }}
                              className="rounded-full bg-red-600 px-2 py-1 font-semibold text-white hover:bg-red-700"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDisconnect(null)}
                              className="rounded-full bg-zinc-200 px-2 py-1 font-semibold text-zinc-700 hover:bg-zinc-300"
                            >
                              No
                            </button>
                          </span>
                        ) : !canDisconnect ? (
                          // Connected, but this role may not disconnect it.
                          <span
                            title={`${item.label} is connected`}
                            className="rounded-full bg-green-700 p-2 text-xs"
                          >
                            <FaCheck size={10} />
                          </span>
                        ) : (
                          <button
                            onClick={() =>
                              setConfirmDisconnect(item.unique_index)
                            }
                            title={`Connected. Click to disconnect ${item.label}`}
                            aria-label={`Disconnect ${item.label}`}
                            className="rounded-full bg-green-700 p-2 text-xs transition-all duration-300 hover:bg-green-800"
                          >
                            <FaCheck size={10} />
                          </button>
                        )}
                      </div>
                    ))}
                    {/* Apps to connect: only for roles that may connect one. */}
                    {(canConnect ? filteredNotIntegratedApps : []).map(
                      (item) => (
                        <div
                          key={`Not-integrated-app-${item.unique_index}`}
                          className="flex w-fit items-center gap-2 rounded-full border border-zinc-400 p-1"
                        >
                          <div className="rounded-full bg-zinc-200 p-1">
                            <img
                              alt={item.label}
                              src={item.image}
                              className="h-5 w-5"
                            />
                          </div>
                          <div className="text-p-text text-md ml-1 text-center font-semibold">
                            {item.label}
                          </div>
                          <button
                            onClick={() => handleIntegrateButtonClick(item)}
                            className="ml-4 rounded-full bg-zinc-100 p-2 text-xs font-bold text-zinc-700 transition-all duration-300 hover:bg-zinc-300"
                          >
                            <MdOutlineAdd size={10} />
                          </button>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {/* ===================== CUSTOM MCP TAB ===================== */}
                {ENABLE_MCP && activeTab === 'Custom MCP' && (
                  <McpConnectionManager />
                )}
              </>
            )}
          </div>

          {isModalOpen && (
            <>
              {modalAppName === 'jira' ? (
                <JiraResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === 'confluence' ? (
                <ConfluenceResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === 'microsoft_sharepoint' ? (
                <SharePointSitesModal
                  onClose={closeModal}
                  onChangeAccess={connectSharePoint}
                />
              ) : modalAppName === 'gohighlevel' &&
                ENABLE_INTEGRATION_GOHIGHLEVEL ? (
                <GohighlevelResourceModal onClose={closeModal} />
              ) : modalAppName === 'shiprocket' &&
                ENABLE_INTEGRATION_SHIPROCKET ? (
                <ShiprocketAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : modalAppName === 'razorpay' && ENABLE_INTEGRATION_RAZORPAY ? (
                <RazorpayAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : (
                <ResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onlyEmailApps={onlyEmailApps}
                />
              )}
            </>
          )}
          {isOutlookSyncOpen && (
            <OutlookSyncModal
              onContinue={handleOutlookSyncContinue}
              onClose={() => setIsOutlookSyncOpen(false)}
            />
          )}
          {isSharePointAccessOpen && (
            <SharePointAccessModal
              onContinue={handleSharePointContinue}
              onClose={() => setIsSharePointAccessOpen(false)}
            />
          )}
          <QuickBooksEnvModal
            isOpen={isQuickBooksEnvModalOpen}
            onClose={() => setIsQuickBooksEnvModalOpen(false)}
            onSelect={handleQuickBooksEnvSelect}
          />
        </div>
      )}
    </div>
  );
};

export default Integration;
